import { useGetPrize } from "@/shared/api/prize";
import { vars } from "@/shared/lib";
import { Column, Row, Table, Typography } from "@/shared/ui";
import { createFileRoute } from "@tanstack/react-router";
import {
	AccessorKeyColumnDef,
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { Suspense, useMemo } from "react";

export const Route = createFileRoute("/(auth)/_layout/prize/")({
	component: Awards,
});

function Awards() {
	return (
		<Suspense>
			<AwardsTable />
		</Suspense>
	);
}

interface PrizeRowData {
	main: string;
	category: string;
	sub: string;
	showMainCategory: boolean;
	showCategory: boolean;
	rowSpan: number;
	categoryRowSpan: number;
	[key: string]: string | boolean | number;
}

interface TableChunk {
	years: string[];
	headers: string[];
}

function AwardsTable() {
	const { data } = useGetPrize();
	const { tableData, headers, sortedYears, rowDefinitions } =
		data.tableData;

	// 테이블 데이터 변환
	const tableRows = useMemo<PrizeRowData[]>(() => {
		const processedMainCategories = new Set<string>();
		const processedCategories = new Map<string, Set<string>>();

		// 각 main별로 전체 row 개수 미리 계산
		const mainCategoryRowCount = new Map<string, number>();
		rowDefinitions.forEach(({ main }) => {
			const count = rowDefinitions.filter(r => r.main === main).length;
			mainCategoryRowCount.set(main, count);
		});

		return rowDefinitions.map(({ main, category, sub }) => {
			const showMainCategory = !processedMainCategories.has(main);
			if (showMainCategory) {
				processedMainCategories.add(main);
			}

			// 각 main별로 category 추적
			if (!processedCategories.has(main)) {
				processedCategories.set(main, new Set());
			}
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			const mainCategories = processedCategories.get(main)!;
			const showCategory = !mainCategories.has(category);
			if (showCategory) {
				mainCategories.add(category);
			}

			// 같은 category의 sub 개수 계산
			const catRowSpan = rowDefinitions.filter(r => r.main === main && r.category === category).length;
			// 같은 main의 전체 row 개수
			const mainRowSpan = mainCategoryRowCount.get(main) || 0;

			const row: PrizeRowData = {
				main,
				category,
				sub,
				showMainCategory,
				showCategory,
				rowSpan: showMainCategory ? mainRowSpan : 0,
				categoryRowSpan: catRowSpan,
			};

			// 각 연도별 데이터 추가
			sortedYears.forEach((year) => {
				row[year] = tableData[main]?.[category]?.[sub]?.[year] || "";
			});

			return row;
		});
	}, [tableData, rowDefinitions, sortedYears]);

	// 5년 단위로 연도 및 헤더 분할 (최신 연도 청크가 먼저 표시)
	const tableChunks = useMemo<TableChunk[]>(() => {
		const chunks: TableChunk[] = [];
		for (let i = 0; i < sortedYears.length; i += 5) {
			const yearsChunk = sortedYears.slice(i, i + 5);
			const headerChunk = yearsChunk.map(
				(year) => headers[sortedYears.indexOf(year)]
			);
			chunks.push({
				years: yearsChunk,
				headers: headerChunk,
			});
		}
		return chunks.reverse();
	}, [sortedYears, headers]);

	// 컬럼 정의 (모든 테이블 청크를 위한 최대 컬럼)
	const columnHelper = createColumnHelper<PrizeRowData>();
	const baseColumns = useMemo(
		() => [
			columnHelper.accessor("main", {
				header: () => "구분",
				size: 120,
				cell: (info) =>
					<Typography size={vars.size[14]} fontWeight="normal">
						{info.row.original.showMainCategory ? info.getValue() : null}
					</Typography>
			}),
			columnHelper.accessor("category", {
				header: () => "카테고리",
				size: 120,
				cell: (info) => (
					<Typography size={vars.size[14]} fontWeight="normal" style={{ textAlign: "left" }}>
						{info.row.original.showCategory ? info.getValue() : null}
					</Typography>
				),
			}),
			columnHelper.accessor("sub", {
				header: () => "상세",
				size: 120,
				cell: (info) => (
					<Typography size={vars.size[14]} fontWeight="normal" style={{ textAlign: "left" }}>
						{info.getValue()}
					</Typography>
				),
			}),
		],
		[columnHelper]
	);

	return (
		<Column fullWidth style={{ gap: vars.size[16] }}>
			{tableChunks.map((chunk, chunkIndex) => (
				<PrizeTableChunk
					key={`chunk-${chunkIndex}`}
					tableRows={tableRows}
					baseColumns={baseColumns}
					columnHelper={columnHelper}
					yearsInChunk={chunk.years}
					headersInChunk={chunk.headers}
				/>
			))}
		</Column>
	);
}

interface PrizeTableChunkProps {
	tableRows: PrizeRowData[];
	baseColumns: AccessorKeyColumnDef<PrizeRowData, string>[]
	columnHelper: ReturnType<typeof createColumnHelper<PrizeRowData>>;
	yearsInChunk: string[];
	headersInChunk: string[];
}

function PrizeTableChunk({
	tableRows,
	baseColumns,
	columnHelper,
	yearsInChunk,
	headersInChunk,
}: PrizeTableChunkProps) {
	const columns = useMemo(() => [
		...baseColumns,
		...yearsInChunk.map((year, index) =>
			columnHelper.accessor((row) => row[year] as string, {
				header: () => headersInChunk[index],
				id: year,
				size: 120,
				cell: (info) => <Typography size={vars.size[14]} fontWeight="normal">
					{info.getValue()}
				</Typography>,
			})
		),
	], [baseColumns, columnHelper, yearsInChunk, headersInChunk]);

	const table = useReactTable({
		data: tableRows,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<Row fullWidth style={{ overflow: "auto", padding: vars.size[4] }}>
			<Table style={{ width: "100%" }}>
				<Table.Header>
					{table.getHeaderGroups().map((headerGroup) => (
						<Table.TR key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								// "구분", "카테고리", "상세" 헤더를 병합
								if (header.id === "main") {
									return (
										<Table.TH
											key={header.id}
											style={{
												minWidth: `${header.getSize() + 240}px`,
											}}
											colSpan={3}
										>
											{"구분"}
										</Table.TH>
									);
								}
								// "카테고리"와 "상세" 헤더는 스킵
								if (header.id === "category" || header.id === "sub") {
									return null;
								}

								return (
									<Table.TH
										key={header.id}
										style={{
											minWidth: `${header.getSize()}px`,
										}}
									>
										{header.isPlaceholder
											? null
											: flexRender(
												header.column.columnDef
													.header,
												header.getContext()
											)}
									</Table.TH>
								);
							})}
						</Table.TR>
					))}
				</Table.Header>
				<Table.Body>
					{table.getRowModel().rows.map((row) => (
						<Table.TR key={row.id}>
							{row.getVisibleCells().map((cell) => {
								const isMainCategoryCell =
									cell.column.id === "main" &&
									row.original.showMainCategory;
								const isHiddenMainCategory =
									cell.column.id === "main" &&
									!row.original.showMainCategory;
								const isCategoryCell =
									cell.column.id === "category" &&
									row.original.showCategory;
								const isHiddenCategory =
									cell.column.id === "category" &&
									!row.original.showCategory;

								return (
									<Table.TD
										key={cell.id}
										rowSpan={
											isMainCategoryCell
												? row.original.rowSpan
												: isCategoryCell
													? row.original.categoryRowSpan
													: undefined
										}
										style={
											isHiddenMainCategory || isHiddenCategory
												? {
													display:
														"none",
												}
												: undefined
										}
									>
										{flexRender(
											cell.column.columnDef
												.cell,
											cell.getContext()
										)}
									</Table.TD>
								);
							})}
						</Table.TR>
					))}
				</Table.Body>
			</Table>
		</Row>
	);
}
